<script>
  import { onMount } from "svelte";
  import { isCommandCenterReady } from "./command-center-ready.mjs";
  import { command_center_apps, command_center_session } from "./stores.js";
  import { hasSafeCommandCenterServiceWorker, isCurrentCommandCenterSession } from "./command-center-session.mjs";

  export let app;
  export let onRetry = () => {};
  let iframe;
  let ready = false;
  let readyCount = 0;
  let deliveryGeneration = 0;
  let error = "";

  async function deliverSession(session) {
    const generation = ++deliveryGeneration;
    if (!ready || session.status !== "ready" ||
        $command_center_apps.status !== "ready" ||
        !$command_center_apps.apps.some((allowed) => allowed.id === app.id) ||
        !isCurrentCommandCenterSession(session)) return;
    try {
      if (!await hasSafeCommandCenterServiceWorker()) {
        error = "Actualiza el portal: el service worker seguro debe estar instalado y controlar esta página antes de compartir la sesión.";
        return;
      }
      if (generation !== deliveryGeneration || !ready ||
          !isCurrentCommandCenterSession(session) ||
          $command_center_apps.status !== "ready" ||
          !$command_center_apps.apps.some((allowed) => allowed.id === app.id)) return;
      iframe.contentWindow.postMessage({
        type: "command-center:session", version: 1,
        jwt: session.jwt, sessionKey: session.sessionKey, expiresAt: session.expiresAt,
      }, app.origin);
    } catch (_error) {
      error = "No se pudo verificar la seguridad del portal para compartir la sesión.";
    }
  }

  $: if (readyCount > 0 && $command_center_session && $command_center_apps) {
    deliverSession($command_center_session);
  }

  onMount(() => {
    const onMessage = (event) => {
      if (!isCommandCenterReady(event, iframe && iframe.contentWindow, app.origin)) return;
      ready = true;
      readyCount++;
      error = "";
    };
    window.addEventListener("message", onMessage);
    const timer = setTimeout(() => {
      if (!ready) {
        error = "La aplicación no confirmó su carga. Comprueba si permite abrirse en un iframe y envía el mensaje de listo.";
      }
    }, 15000);
    // El listener está instalado antes de iniciar la navegación, incluso en cargas rápidas.
    iframe.src = app.startUrl;
    return () => {
      deliveryGeneration++;
      ready = false;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      iframe.src = "about:blank";
    };
  });
</script>

<main role="main" class="command-center mdc-top-app-bar--dense-fixed-adjust">
  <header class="command-center__header">
    <img src={app.iconUrl} alt="" referrerpolicy="no-referrer" on:error={(event) => { event.currentTarget.style.display = "none"; }} />
    <h1>{app.name}</h1>
    <span class:ready class="command-center__status" role="status">
      {ready ? "Lista" : error ? "Sin confirmación" : "Cargando…"}
    </span>
  </header>
  {#if error}
    <div class="command-center__warning" role="alert">
      {error}
      <button type="button" on:click={onRetry}>Reintentar</button>
    </div>
  {/if}
  <div class="command-center__viewport" aria-busy={!ready}>
  <iframe
    bind:this={iframe}
    src="about:blank"
    title={app.name}
    inert={ready ? undefined : ""}
    tabindex={ready ? 0 : -1}
    sandbox="allow-scripts allow-forms allow-same-origin"
    referrerpolicy="no-referrer"
    class="command-center__frame"
  ></iframe>
  {#if !ready}
    <div class="command-center__loading" role="status" aria-label="Cargando aplicación">
      <div class="command-center__spinner" aria-hidden="true"></div>
    </div>
  {/if}
  </div>
</main>

<style>
  .command-center {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    height: 100vh;
    min-height: 0;
    background: #f4f7fb;
  }
  .command-center__header {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.8rem 1.25rem;
    color: #17324d;
    background: #fff;
    border-bottom: 1px solid #dee6ef;
  }
  .command-center__header img { width: 2rem; height: 2rem; object-fit: contain; }
  .command-center__header h1 { flex: 1; min-width: 0; overflow-wrap: anywhere; font-size: 1.15rem; margin: 0; }
  .command-center__status { color: #94622b; font-size: 0.85rem; }
  .command-center__status.ready { color: #176247; }
  .command-center__warning {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: #fff3da;
    color: #784e14;
    padding: 0.6rem 1.25rem;
  }
  .command-center__warning button { border: 1px solid #b78b4d; background: #fff; border-radius: 0.4rem; padding: 0.25rem 0.7rem; }
  .command-center__viewport { position: relative; display: flex; flex: 1; min-height: 0; }
  .command-center__frame { flex: 1; width: 100%; min-width: 0; min-height: 0; border: 0; background: #fff; }
  .command-center__loading { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(244, 247, 251, 0.65); }
  .command-center__spinner { width: 3rem; height: 3rem; border-radius: 50%; border: 0.28rem solid #dbeafe; border-top-color: #2563eb; animation: command-center-spin 0.9s linear infinite; }
  @keyframes command-center-spin { to { transform: rotate(360deg); } }
</style>